import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { AlertCircle, Loader2 } from 'lucide-react';

/**
 * Buy credits — a partner paying for their own wallet, through Razorpay.
 *
 * THREE NUMBERS, AND THE PARTNER SEES ALL THREE.
 * Face value is what they get. Cash payable is face less their discount. Charged is that
 * plus 18% GST, and it is the only one that leaves their bank. Showing only the charge
 * hides whether their discount was applied at all, which for a partner on negotiated terms
 * is the one thing they will check.
 *
 * THE PRICE IS NEVER COMPUTED HERE.
 * It comes from the platform, which reads the card THIS partner is pinned to. A reseller
 * signed at a ₹10,000 floor buys at ₹10,000 while the published floor is ₹25,000, and a
 * discount hardcoded in a browser would be the published one for everybody.
 *
 * PAYING IS NOT CREDITING.
 * Razorpay's success callback means the payment went through, not that the wallet exists.
 * The platform books it from the signed webhook, which lands separately and can be a moment
 * behind. So this closes on "payment received" and refreshes — it never claims a balance it
 * has not seen.
 */

const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(true);
  return new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = RAZORPAY_SRC;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const BuyCreditsDialog = ({ open, onOpenChange, onPaid, partnerName, partnerEmail }) => {
  const { getAuthHeaders, API_URL } = useAuth();
  const [faceValue, setFaceValue] = useState('');
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [refusal, setRefusal] = useState('');
  const [paying, setPaying] = useState(false);
  const quoteSeq = useRef(0);

  const money = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
      .format(n || 0);

  const fetchQuote = useCallback(async (value) => {
    const amount = Number(value);
    if (!amount || amount <= 0) { setQuote(null); setRefusal(''); return; }
    /* Every keystroke starts a request and they can land out of order — a stale one
       overwriting a fresh one would show the wrong price against the typed amount. */
    const seq = ++quoteSeq.current;
    setQuoting(true);
    try {
      const res = await axios.get(`${API_URL}/api/partner/credits/quote`, {
        params: { face_value: amount },
        headers: getAuthHeaders(),
      });
      if (seq !== quoteSeq.current) return;
      setQuote(res.data);
      setRefusal('');
    } catch (error) {
      if (seq !== quoteSeq.current) return;
      setQuote(null);
      setRefusal(error?.response?.data?.detail || 'We could not price that just now.');
    } finally {
      if (seq === quoteSeq.current) setQuoting(false);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    const t = setTimeout(() => fetchQuote(faceValue), 400);
    return () => clearTimeout(t);
  }, [faceValue, fetchQuote]);

  useEffect(() => {
    if (!open) { setFaceValue(''); setQuote(null); setRefusal(''); }
  }, [open]);

  const pay = async () => {
    setPaying(true);
    try {
      if (!(await loadRazorpay())) {
        toast.error('Could not load the payment window. Check your connection and try again.');
        return;
      }
      const res = await axios.post(
        `${API_URL}/api/partner/credits/order`,
        { face_value: Number(faceValue) },
        { headers: getAuthHeaders() }
      );
      const order = res.data;

      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        order_id: order.order_id,
        amount: order.amount_paise,
        currency: order.currency || 'INR',
        name: 'Expedify',
        description: `${money(order.face_value)} of credits`,
        prefill: { name: partnerName || '', email: partnerEmail || '' },
        theme: { color: '#ef785b' },
        handler: async (response) => {
          /* Paid. Whether it is CREDITED is a separate question, and this does not assume
             the answer: the platform reads the payment back from Razorpay and books it. The
             webhook usually wins this race, in which case confirm reports a duplicate — the
             right outcome, not an error. What this covers is the webhook that never comes:
             no tunnel in local development, a rotated secret, an outage. Without it a
             partner who has paid sits with no credits and no way back. */
          onOpenChange(false);
          try {
            const confirmed = await axios.post(
              `${API_URL}/api/partner/credits/confirm`,
              { order_id: response?.razorpay_order_id || order.order_id },
              { headers: getAuthHeaders() }
            );
            toast.success(
              confirmed.data?.booked
                ? 'Payment received — your credits are in.'
                : 'Payment received. Your credits appear shortly.'
            );
          } catch {
            /* The payment went through; only the confirmation call did not. Never tell a
               partner their payment failed when their money has left. */
            toast.success('Payment received. Your credits appear shortly.');
          }
          onPaid?.();
        },
        modal: {
          ondismiss: () => {
            /* An abandoned checkout costs nothing and mints nothing — no order cleanup
               needed, because nothing was written. */
            setPaying(false);
          },
        },
      });
      rzp.on('payment.failed', (e) => {
        toast.error(e?.error?.description || 'The payment did not go through.');
      });
      rzp.open();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not start the payment.');
    } finally {
      setPaying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="buy-credits-dialog">
        <DialogHeader>
          <DialogTitle>Buy credits</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-[#1A1A1A] block mb-1.5">
              Credits to buy (face value)
            </label>
            <Input
              type="number"
              min="0"
              step="1000"
              value={faceValue}
              onChange={(e) => setFaceValue(e.target.value)}
              placeholder="50000"
              className="h-10 border-[#E2E2DF]"
              data-testid="buy-credits-face-value"
            />
            {quote?.floor ? (
              <p className="mt-1.5 text-xs text-[#64748B]">
                Your minimum purchase is {money(quote.floor)}.
              </p>
            ) : null}
          </div>

          {refusal ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
                 data-testid="buy-credits-refusal">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs text-[#64748B]">{refusal}</p>
            </div>
          ) : null}

          {quote ? (
            <div className="rounded-lg border border-[#E2E2DF] bg-[#FAFAF8] p-3 text-sm"
                 data-testid="buy-credits-quote">
              <Row label="Credits you get" value={money(quote.face_value)} />
              <Row
                label={`Your ${quote.tier} discount (${Math.round((quote.discount_pct || 0) * 100)}%)`}
                value={`− ${money(quote.face_value - quote.cash_payable)}`}
              />
              <Row label="Taxable value" value={money(quote.cash_payable)} />
              <Row label="GST 18%" value={money(quote.gst_amount)} />
              <div className="mt-2 border-t border-[#E2E2DF] pt-2">
                <Row
                  label="You pay"
                  value={money(quote.amount_charged)}
                  strong
                  testId="buy-credits-total"
                />
              </div>
              <p className="mt-2 text-[11px] text-[#64748B]">
                Credits expire {new Date(quote.expires_at).toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}. A GST invoice is issued the moment the payment lands.
              </p>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" className="border-[#E2E2DF]" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={pay}
            disabled={!quote || quoting || paying}
            className="bg-[#ef785b] hover:bg-[#D44017] text-white"
            data-testid="buy-credits-pay"
          >
            {paying || quoting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {quote ? `Pay ${money(quote.amount_charged)}` : 'Pay'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value, strong, testId }) => (
  <div className="flex items-center justify-between gap-3 py-0.5">
    <span className={strong ? 'font-medium text-[#1A1A1A]' : 'text-[#64748B]'}>{label}</span>
    <span
      className={`font-mono tabular-nums ${strong ? 'font-semibold text-[#1A1A1A]' : 'text-[#1A1A1A]'}`}
      data-testid={testId}
    >
      {value}
    </span>
  </div>
);

export default BuyCreditsDialog;
